import { useCallback, useEffect, useMemo, useState } from "react"

import type {
  FeasibilityValue,
  ImplTime,
  OpCostUnit,
  StatusIndex,
  TimeSavedUnit,
  ToggleValue,
  UseCaseFormData,
} from "../../types/use-case.types"

import { SettingsService } from "@/services/settings-service"
import { useUseCasesStore } from "@/stores/use-cases-store"

import { FactSheetActions } from "./fact-sheet-actions"
import { FactSheetAICards } from "./fact-sheet-ai-cards"
import { FactSheetCategories } from "./fact-sheet-categories"
import { FactSheetDescriptions } from "./fact-sheet-descriptions"
import { FactSheetHeader } from "./fact-sheet-header"
import { FactSheetKPIs } from "./fact-sheet-kpis"
import { FactSheetParameters } from "./fact-sheet-parameters"
import { FactSheetSidebarFields } from "./fact-sheet-sidebar-fields"
import { UseCaseSelector } from "./use-case-selector"
import { useAiQaReview } from "../../hooks/use-ai-qa-review"
import { useAiSalesPitch } from "../../hooks/use-ai-sales-pitch"
import { useCreateUseCase } from "../../hooks/use-create-use-case"
import { useDeleteUseCase } from "../../hooks/use-delete-use-case"
import { useUpdateUseCase } from "../../hooks/use-update-use-case"
import { useUseCase } from "../../hooks/use-use-case"
import { useUseCaseCalculations } from "../../hooks/use-use-case-calculations"
import { NewUseCaseWizard } from "../wizard/new-use-case-wizard"

const DEFAULTS = {
  ucName: "",
  owner: "",
  timeSavedHours: "0",
  users: "0",
  salary: "70000",
  directYear: "0",
  investment: "0",
  opCost: "0",
  timeSavedUnit: "week" as TimeSavedUnit,
  opCostUnit: "monthly" as OpCostUnit,
  implTime: "" as ImplTime,
  issue: "",
  solutionDesc: "",
  dataNeeded: "",
  comments: "",
  bpSel: [] as string[],
  saSel: [] as string[],
  efSel: [] as string[],
  siteAreaSel: [] as string[],
  feasV: "2" as FeasibilityValue,
  statIdx: 2 as StatusIndex,
  togTS: "yes" as ToggleValue,
  togQI: "yes" as ToggleValue,
  togRD: "no" as ToggleValue,
  togWW: "yes" as ToggleValue,
}

export function FactSheetTab() {
  const { selectedUseCaseId, setSelectedUseCaseId } = useUseCasesStore()
  const { data: selectedUseCase } = useUseCase(selectedUseCaseId)
  const createMutation = useCreateUseCase()
  const updateMutation = useUpdateUseCase()
  const deleteMutation = useDeleteUseCase()
  // Form state
  const [ucName, setUcName] = useState(DEFAULTS.ucName)
  const [owner, setOwner] = useState(DEFAULTS.owner)
  const [timeSavedHours, setTimeSavedHours] = useState(DEFAULTS.timeSavedHours)
  const [users, setUsers] = useState(DEFAULTS.users)
  const [salary, setSalary] = useState(DEFAULTS.salary)
  const [directYear, setDirectYear] = useState(DEFAULTS.directYear)
  const [investment, setInvestment] = useState(DEFAULTS.investment)
  const [opCost, setOpCost] = useState(DEFAULTS.opCost)
  const [timeSavedUnit, setTimeSavedUnit] = useState<TimeSavedUnit>(DEFAULTS.timeSavedUnit)
  const [opCostUnit, setOpCostUnit] = useState<OpCostUnit>(DEFAULTS.opCostUnit)
  const [implTime, setImplTime] = useState<ImplTime>(DEFAULTS.implTime)
  const [issue, setIssue] = useState(DEFAULTS.issue)
  const [solutionDesc, setSolutionDesc] = useState(DEFAULTS.solutionDesc)
  const [dataNeeded, setDataNeeded] = useState(DEFAULTS.dataNeeded)
  const [comments, setComments] = useState(DEFAULTS.comments)
  const [bpSel, setBpSel] = useState<string[]>(DEFAULTS.bpSel)
  const [saSel, setSaSel] = useState<string[]>(DEFAULTS.saSel)
  const [efSel, setEfSel] = useState<string[]>(DEFAULTS.efSel)
  const [siteAreaSel, setSiteAreaSel] = useState<string[]>(DEFAULTS.siteAreaSel)
  const [feasV, setFeasV] = useState<FeasibilityValue>(DEFAULTS.feasV)
  const [statIdx, setStatIdx] = useState<StatusIndex>(DEFAULTS.statIdx)
  const [togTS, setTogTS] = useState<ToggleValue>(DEFAULTS.togTS)
  const [togQI, setTogQI] = useState<ToggleValue>(DEFAULTS.togQI)
  const [togRD, setTogRD] = useState<ToggleValue>(DEFAULTS.togRD)
  const [togWW, setTogWW] = useState<ToggleValue>(DEFAULTS.togWW)

  // Real-time calculations
  const kpis = useUseCaseCalculations({
    timeSavedHours,
    timeSavedUnit,
    users,
    salary,
    directYear,
    investment,
    opCost,
    opCostUnit,
  })

  // Populate form when selected use case changes
  useEffect(() => {
    if (selectedUseCase) {
      setUcName(selectedUseCase.ucName)
      setOwner(selectedUseCase.owner)
      setTimeSavedHours(selectedUseCase.timeSavedHours)
      setUsers(selectedUseCase.users)
      setSalary(selectedUseCase.salary)
      setDirectYear(selectedUseCase.directYear)
      setInvestment(selectedUseCase.investment)
      setOpCost(selectedUseCase.opCost)
      setTimeSavedUnit(selectedUseCase.timeSavedUnit)
      setOpCostUnit(selectedUseCase.opCostUnit)
      setImplTime(selectedUseCase.implTime)
      setIssue(selectedUseCase.issue)
      setSolutionDesc(selectedUseCase.solutionDesc)
      setDataNeeded(selectedUseCase.dataNeeded)
      setComments(selectedUseCase.comments)
      setBpSel(selectedUseCase.bpSel)
      setSaSel(selectedUseCase.saSel)
      setEfSel(selectedUseCase.efSel)
      setSiteAreaSel(selectedUseCase.siteAreaSel)
      setFeasV(selectedUseCase.feasV)
      setStatIdx(selectedUseCase.statIdx)
      setTogTS(selectedUseCase.togTS)
      setTogQI(selectedUseCase.togQI)
      setTogRD(selectedUseCase.togRD)
      setTogWW(selectedUseCase.togWW)
    }
  }, [selectedUseCase])

  const resetForm = useCallback(() => {
    setUcName(DEFAULTS.ucName)
    setOwner(DEFAULTS.owner)
    setTimeSavedHours(DEFAULTS.timeSavedHours)
    setUsers(DEFAULTS.users)
    setSalary(DEFAULTS.salary)
    setDirectYear(DEFAULTS.directYear)
    setInvestment(DEFAULTS.investment)
    setOpCost(DEFAULTS.opCost)
    setTimeSavedUnit(DEFAULTS.timeSavedUnit)
    setOpCostUnit(DEFAULTS.opCostUnit)
    setImplTime(DEFAULTS.implTime)
    setIssue(DEFAULTS.issue)
    setSolutionDesc(DEFAULTS.solutionDesc)
    setDataNeeded(DEFAULTS.dataNeeded)
    setComments(DEFAULTS.comments)
    setBpSel(DEFAULTS.bpSel)
    setSaSel(DEFAULTS.saSel)
    setEfSel(DEFAULTS.efSel)
    setSiteAreaSel(DEFAULTS.siteAreaSel)
    setFeasV(DEFAULTS.feasV)
    setStatIdx(DEFAULTS.statIdx)
    setTogTS(DEFAULTS.togTS)
    setTogQI(DEFAULTS.togQI)
    setTogRD(DEFAULTS.togRD)
    setTogWW(DEFAULTS.togWW)
    setSelectedUseCaseId(null)
  }, [setSelectedUseCaseId])

  // Field setter map
  const handleChange = useCallback((field: string, value: string | string[] | number) => {
    const setters: Record<string, (v: never) => void> = {
      ucName: setUcName as (v: never) => void,
      owner: setOwner as (v: never) => void,
      timeSavedHours: setTimeSavedHours as (v: never) => void,
      users: setUsers as (v: never) => void,
      salary: setSalary as (v: never) => void,
      directYear: setDirectYear as (v: never) => void,
      investment: setInvestment as (v: never) => void,
      opCost: setOpCost as (v: never) => void,
      timeSavedUnit: setTimeSavedUnit as (v: never) => void,
      opCostUnit: setOpCostUnit as (v: never) => void,
      implTime: setImplTime as (v: never) => void,
      issue: setIssue as (v: never) => void,
      solutionDesc: setSolutionDesc as (v: never) => void,
      dataNeeded: setDataNeeded as (v: never) => void,
      comments: setComments as (v: never) => void,
      bpSel: setBpSel as (v: never) => void,
      saSel: setSaSel as (v: never) => void,
      efSel: setEfSel as (v: never) => void,
      siteAreaSel: setSiteAreaSel as (v: never) => void,
      feasV: setFeasV as (v: never) => void,
      statIdx: ((v: number) => setStatIdx(v as StatusIndex)) as (v: never) => void,
      togTS: setTogTS as (v: never) => void,
      togQI: setTogQI as (v: never) => void,
      togRD: setTogRD as (v: never) => void,
      togWW: setTogWW as (v: never) => void,
    }
    const setter = setters[field]
    setter(value as never)
  }, [])

  const getFormData = useCallback((): UseCaseFormData => {
    return {
      ucName,
      owner,
      timeSavedHours,
      users,
      salary,
      directYear,
      investment,
      opCost,
      timeSavedUnit,
      opCostUnit,
      implTime,
      issue,
      solutionDesc,
      dataNeeded,
      comments,
      bpSel,
      saSel,
      efSel,
      siteAreaSel,
      feasV,
      statIdx,
      togTS,
      togQI,
      togRD,
      togWW,
    }
  }, [
    ucName,
    owner,
    timeSavedHours,
    users,
    salary,
    directYear,
    investment,
    opCost,
    timeSavedUnit,
    opCostUnit,
    implTime,
    issue,
    solutionDesc,
    dataNeeded,
    comments,
    bpSel,
    saSel,
    efSel,
    siteAreaSel,
    feasV,
    statIdx,
    togTS,
    togQI,
    togRD,
    togWW,
  ])

  const handleSave = useCallback(async () => {
    const data = getFormData()
    if (selectedUseCaseId) {
      await updateMutation.mutateAsync({ id: selectedUseCaseId, data })
    } else {
      const result = await createMutation.mutateAsync(data)
      setSelectedUseCaseId(result.id)
    }
  }, [getFormData, selectedUseCaseId, updateMutation, createMutation, setSelectedUseCaseId])

  const handleDelete = useCallback(async () => {
    if (!selectedUseCaseId) return
    await deleteMutation.mutateAsync(selectedUseCaseId)
    resetForm()
  }, [selectedUseCaseId, deleteMutation, resetForm])

  const handleImport = useCallback(
    (data: Record<string, unknown>) => {
      const fields = data as Record<string, string | string[] | number>
      for (const [key, value] of Object.entries(fields)) {
        handleChange(key, value)
      }
    },
    [handleChange]
  )

  const handleExport = useCallback((): Record<string, unknown> => {
    return getFormData()
  }, [getFormData])

  // AI mutations (shared between action bar and inline cards)
  const isUseCaseAdmin = SettingsService.getInstance().settings.user_policy.is_usecase_admin
  const qaReview = useAiQaReview()
  const salesPitch = useAiSalesPitch()

  // Build persisted QA result from saved use case data (used as fallback when no fresh mutation data)
  const persistedQaResult = useMemo(() => {
    if (!selectedUseCase?.qaScore || !selectedUseCase.qaReviewData) return null
    return {
      success: true,
      score: selectedUseCase.qaScore,
      grade: selectedUseCase.qaGrade ?? "N/A",
      summary: selectedUseCase.qaReviewData.summary,
      issues: selectedUseCase.qaReviewData.issues,
      suggestions: selectedUseCase.qaReviewData.suggestions,
    }
  }, [selectedUseCase?.qaScore, selectedUseCase?.qaGrade, selectedUseCase?.qaReviewData])

  const triggerQaReview = useCallback(() => {
    qaReview.mutate({ useCaseData: getFormData(), useCaseId: selectedUseCaseId ?? undefined })
  }, [getFormData, selectedUseCaseId, qaReview])

  const triggerSalesPitch = useCallback(() => {
    salesPitch.mutate({ useCaseData: getFormData(), useCaseId: selectedUseCaseId ?? undefined })
  }, [getFormData, selectedUseCaseId, salesPitch])

  const isSaving = createMutation.isPending || updateMutation.isPending

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false)

  const handleWizardComplete = useCallback(
    async (data: UseCaseFormData) => {
      // If empty object (scratch path), just reset to new
      if (!data.ucName && !data.issue) {
        resetForm()
        return
      }
      // Inject owner if not set
      const userName = SettingsService.getInstance().claims.name
      const formData: UseCaseFormData = { ...data, owner: data.owner || userName || "" }
      // Create the use case in the backend
      const result = await createMutation.mutateAsync(formData)
      // Load the newly created use case into the form
      setSelectedUseCaseId(result.id)
    },
    [resetForm, createMutation, setSelectedUseCaseId]
  )

  return (
    <div className="space-y-3">
      {/* Wizard Dialog */}
      <NewUseCaseWizard
        open={wizardOpen}
        onComplete={handleWizardComplete}
        onOpenChange={setWizardOpen}
      />

      {/* Selector */}
      <UseCaseSelector onNewUseCase={() => setWizardOpen(true)} />

      {/* Actions */}
      <FactSheetActions
        currentUserRole={selectedUseCase?.currentUserRole}
        getFormData={getFormData}
        isEditing={!!selectedUseCaseId}
        isSaving={isSaving}
        qaReview={isUseCaseAdmin ? qaReview : undefined}
        salesPitch={salesPitch}
        ucName={ucName}
        useCase={selectedUseCase}
        useCaseId={selectedUseCaseId ?? undefined}
        onDelete={handleDelete}
        onExport={handleExport}
        onImport={handleImport}
        onReset={resetForm}
        onRunQa={isUseCaseAdmin ? triggerQaReview : undefined}
        onRunSalesPitch={triggerSalesPitch}
        onSave={handleSave}
      />

      {/* Header: Name, Owner, Site/Area */}
      <FactSheetHeader
        owner={owner}
        siteAreaSel={siteAreaSel}
        ucName={ucName}
        onChange={handleChange}
      />

      {/* Main content: two columns */}
      <div className="grid grid-cols-[1fr_220px] gap-3">
        {/* Left column */}
        <div className="space-y-3">
          {/* Descriptions + KPIs */}
          <div className="grid grid-cols-2 gap-3">
            <FactSheetDescriptions
              dataNeeded={dataNeeded}
              getFormData={getFormData}
              issue={issue}
              solutionDesc={solutionDesc}
              onChange={handleChange}
            />
            <FactSheetKPIs investment={investment} kpis={kpis} />
          </div>

          {/* Categories */}
          <FactSheetCategories bpSel={bpSel} efSel={efSel} saSel={saSel} onChange={handleChange} />

          {/* AI Cards: QA Review + Sales Pitch */}
          <FactSheetAICards
            qaIsLoading={isUseCaseAdmin ? qaReview.isPending : false}
            qaResult={isUseCaseAdmin ? (qaReview.data ?? persistedQaResult) : null}
            salesPitchIsLoading={salesPitch.isPending}
            salesPitchResult={salesPitch.data?.text ?? selectedUseCase?.salesPitchText ?? null}
            onRunQa={isUseCaseAdmin ? triggerQaReview : undefined}
            onRunSalesPitch={triggerSalesPitch}
          />

          {/* Parameters */}
          <FactSheetParameters
            comments={comments}
            directYear={directYear}
            investment={investment}
            opCost={opCost}
            opCostUnit={opCostUnit}
            salary={salary}
            timeSavedHours={timeSavedHours}
            timeSavedUnit={timeSavedUnit}
            users={users}
            onChange={handleChange}
          />
        </div>

        {/* Right sidebar */}
        <FactSheetSidebarFields
          feasV={feasV}
          implTime={implTime}
          statIdx={statIdx}
          timeSavedHours={timeSavedHours}
          timeSavedUnit={timeSavedUnit}
          togQI={togQI}
          togRD={togRD}
          togTS={togTS}
          togWW={togWW}
          users={users}
          onChange={handleChange}
        />
      </div>
    </div>
  )
}
